<section class="tc-automation" aria-labelledby="tc-automation-heading">
  <header class="tc-automation__header">
    <mat-icon class="tc-automation__header-icon" aria-hidden="true">settings</mat-icon>
    <h3 id="tc-automation-heading" class="tc-automation__title">
      {{ 'testCases.form.automation.blockTitle' | translate }}
    </h3>
  </header>

  <div class="tc-automation__field">
    <div id="tc-automation-id-label" class="tc-automation__label">
      {{ 'testCases.form.automation.idLabel' | translate }}
    </div>

    @if (automationKey(); as key) {
      <div class="tc-automation__id-row">
        <code class="tc-automation__id" aria-labelledby="tc-automation-id-label">{{ key }}</code>
        <button
          type="button"
          class="tc-automation__copy"
          (click)="copyKey()"
          [attr.aria-label]="'testCases.form.automation.copy' | translate"
        >
          <mat-icon class="tc-automation__copy-icon" aria-hidden="true">
            {{ copied() ? 'check' : 'content_copy' }}
          </mat-icon>
          {{ (copied() ? 'testCases.form.automation.copied' : 'testCases.form.automation.copy') | translate }}
        </button>
      </div>
      <p class="tc-automation__hint">{{ 'testCases.form.automation.idHint' | translate }}</p>
    } @else {
      <p class="tc-automation__hint tc-automation__hint--pending">
        {{ 'testCases.form.automation.idPending' | translate }}
      </p>
    }
  </div>

  <div class="tc-automation__field">
    <mat-form-field
      appearance="outline"
      floatLabel="always"
      class="tc-automation__select-field tc-automation__field--uppercase-label"
    >
      <mat-label>{{ 'testCases.form.automation.frameworkLabel' | translate }}</mat-label>
      <mat-select [formControl]="frameworkControl()">
        <mat-option [value]="null">{{ 'testCases.form.automation.frameworkNone' | translate }}</mat-option>
        @for (option of frameworkOptions; track option.value) {
          <mat-option [value]="option.value">{{ option.labelKey | translate }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
  </div>

  <div class="tc-automation__field">
    <mat-form-field
      appearance="outline"
      floatLabel="always"
      class="tc-automation__locator-field tc-automation__field--uppercase-label"
    >
      <mat-label>{{ 'testCases.form.automation.locatorLabel' | translate }}</mat-label>
      <input
        matInput
        type="text"
        data-testid="tc-automation-locator"
        [formControl]="locatorControl()"
        [maxlength]="locatorMaxLength"
        [placeholder]="'testCases.form.automation.locatorPlaceholder' | translate"
      />
    </mat-form-field>
  </div>

  @if (job(); as current) {
    <div class="tc-automation__job" [attr.data-status]="current.status" data-testid="tc-job-status">
      <div class="tc-automation__job-head">
        @if (jobRunning()) {
          <span class="tc-automation__job-spinner" aria-hidden="true"></span>
        }
        <span class="tc-automation__job-label">
          {{
            (cancelRequested() ? 'testCases.run.cancel.requested' : 'testCases.run.jobStatus.' + current.status)
              | translate
          }}
        </span>
        @if (canCancel()) {
          <button
            mat-stroked-button
            type="button"
            class="tc-automation__job-cancel"
            data-testid="tc-job-cancel"
            [disabled]="cancelling() || cancelRequested()"
            (click)="askCancel()"
          >
            @if (cancelling()) {
              <mat-progress-spinner diameter="16" mode="indeterminate" aria-hidden="true" />
            } @else {
              <mat-icon aria-hidden="true">stop_circle</mat-icon>
            }
            {{ 'testCases.run.cancel.button' | translate }}
          </button>
        }
      </div>
      <div class="tc-automation__job-facts">
        @if (current.runnerName) {
          <span class="tc-automation__job-fact" data-testid="tc-job-runner">
            <span class="tc-automation__label">{{ 'testCases.run.job.runner' | translate }}</span>
            {{ current.runnerName }}
          </span>
        } @else if (current.requiredLabels.length > 0) {
          <span class="tc-automation__job-fact" data-testid="tc-job-runner">
            <span class="tc-automation__label">{{ 'testCases.run.job.requirement' | translate }}</span>
            {{ current.requiredLabels.join(', ') }}
          </span>
        }
        @if (current.startedAt) {
          <span class="tc-automation__job-fact" data-testid="tc-job-started">
            <span class="tc-automation__label">{{ 'testCases.run.job.startedAt' | translate }}</span>
            {{ current.startedAt | date: 'short' : undefined : locale() }}
          </span>
          @if (jobDuration(); as duration) {
            <span class="tc-automation__job-fact" data-testid="tc-job-duration">
              <span class="tc-automation__label">{{ 'testCases.run.job.duration' | translate }}</span>
              {{ duration }}
            </span>
          }
        } @else {
          <span class="tc-automation__job-fact" data-testid="tc-job-queued">
            <span class="tc-automation__label">{{ 'testCases.run.job.queuedAt' | translate }}</span>
            {{ current.createdAt | date: 'short' : undefined : locale() }}
          </span>
        }
        @if (!current.requestedByCaller && current.requestedByName) {
          <span class="tc-automation__job-fact" data-testid="tc-job-requested-by">
            {{ 'testCases.run.job.requestedBy' | translate: { name: current.requestedByName } }}
          </span>
        }
      </div>
      @if (current.testRunId && projectId(); as projectId) {
        <a
          class="tc-automation__job-link"
          data-testid="tc-job-run-link"
          [routerLink]="['/projects', projectId, 'test-runs', current.testRunId, 'executions']"
        >
          @if (current.testRunKey) {
            {{ 'testCases.run.job.openRun' | translate: { key: current.testRunKey } }}
          } @else {
            {{ 'testCases.run.job.openRunWithoutKey' | translate }}
          }
        </a>
      }
      @if (current.protocol.length > 0) {
        <pre class="tc-automation__job-log"><code>{{ current.protocol.join('\n') }}</code></pre>
      }
      @if (current.error) {
        <p class="tc-automation__job-error">{{ current.error }}</p>
      }
    </div>
  }
  @if (latestLoadFailed()) {
    <p class="tc-automation__job-error" data-testid="tc-job-load-error" role="alert">
      {{ 'testCases.run.job.loadFailed' | translate }}
    </p>
  }
  @if (cancelError(); as key) {
    <p class="tc-automation__job-error" role="alert" data-testid="tc-job-cancel-error">
      {{ 'testCases.run.cancel.' + key | translate }}
    </p>
  }
  @if (jobError(); as key) {
    <p class="tc-automation__job-error" role="alert">{{ 'testCases.run.' + key | translate }}</p>
  }

  <app-confirm-dialog
    icon="stop_circle"
    [visible]="cancelConfirming()"
    [busy]="cancelling()"
    [title]="'testCases.run.cancel.confirmTitle' | translate"
    [message]="'testCases.run.cancel.confirmMessage' | translate"
    [confirmLabel]="'testCases.run.cancel.confirmConfirm' | translate"
    [cancelLabel]="'testCases.run.cancel.confirmCancel' | translate"
    (confirmed)="confirmCancel()"
    (cancelled)="dismissCancel()"
  />
</section>
